// Globals
import debounce from 'lodash.debounce';
import { ChangeEvent, memo, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Modules
import { useStoresAutocomplete } from '@/modules/Stores';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { changeQuery, getFeedbacks, selectQuery } from '../../feature';

// Components
import { Input, Select } from '@/ui';
import { StyledFilterRow } from './PartnersRequestsFiltersContainerStyles';

// Models
import { FeedbackType } from '@/models/feedback.model';

// Helpers
import { GetFeedbacksDTO } from '../../dtos';
import { FeedbacksFiltersFields } from '../../helpers/types';
import { getStatusTypeOptions } from '../../helpers/getOptions';
import { useManagersOptions } from '../../hooks';

type IFeedbacksFiltersContainerProps = {
  children?: ReactNode;
  className?: string;
};

const PartnersRequestsFiltersContainer = ({ children, className }: IFeedbacksFiltersContainerProps) => {
  const { t } = useTranslation(['feedbacks']);
  const { options, onSearch } = useStoresAutocomplete();
  const dispatch = useAppDispatch();
  const query = useAppState(selectQuery);
  const managersOptions = useManagersOptions();

  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        const dto = new GetFeedbacksDTO({ ...searchParams, type: FeedbackType.PARTNER_SUPPORT });
        dispatch(getFeedbacks(dto));
      }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...query, [name]: value || undefined };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleSelectFilter = (name: string) => (value: string) => {
    const newQuery = {
      ...query,
      [name]: value || undefined,
    };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <Select
            placeholder={t('label_shop')}
            options={options}
            onSearch={onSearch}
            showSearch
            onChange={handleSelectFilter(FeedbacksFiltersFields.MODEL_ID)}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('label_phone')}
            name={FeedbacksFiltersFields.PHONE}
            onChange={handleSearch}
            value={query?.[FeedbacksFiltersFields.PHONE]?.toString()}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('label_email')}
            name={FeedbacksFiltersFields.EMAIL}
            onChange={handleSearch}
            value={query?.[FeedbacksFiltersFields.EMAIL]?.toString()}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('label_comment')}
            name={FeedbacksFiltersFields.COMMENT}
            onChange={handleSearch}
            value={query?.[FeedbacksFiltersFields.COMMENT]?.toString()}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Select
            size="large"
            allowClear
            placeholder={t('label_manager')}
            value={query?.[FeedbacksFiltersFields.MANAGER_ID]}
            onChange={handleSelectFilter(FeedbacksFiltersFields.MANAGER_ID)}
            options={managersOptions}
          />
        </th>
        <th>
          <Select
            size="large"
            allowClear
            placeholder={t('label_status')}
            value={query?.[FeedbacksFiltersFields.STATUS]}
            onChange={handleSelectFilter(FeedbacksFiltersFields.STATUS)}
            options={getStatusTypeOptions(t)}
          />
        </th>
      </StyledFilterRow>
    </thead>
  );
};

export default memo(PartnersRequestsFiltersContainer);
